import React from 'react';
import { FormattedNumber, FormattedList } from 'react-intl';
import { Link } from 'react-router-dom';
import { Clearfix, Label, Tooltip } from 'react-bootstrap';
import { findIndex as _findIndex, find as _find } from 'lodash';
import moment from 'moment';
import 'moment-timezone';

import { getNameInCurrentLanguage } from 'helpers/intl';
import { getColor } from 'helpers/charts';

import OverlayTrigger from 'components/OverlayTrigger';

import IntlFormInlineSelector from 'components/intlForm/IntlFormInlineSelector';

import ClientListInfo from 'components/layout/ClientListInfo';
import IntlDate from 'components/layout/IntlDate';
import IntlDateTime from 'components/layout/IntlDateTime';
import IntlDateRelative from 'components/layout/IntlDateRelative';

// eslint-disable-next-line import/prefer-default-export
export const getLeadRowData = (lead, messages, appIntl, onClick) => {
  const {
    id,
    title,
    pipeline,
    target,
    assignee,
    current_stage: currentStage,
    is_won: isWon,
    is_lost: isLost,
    is_deleted: isDeleted,
  } = lead;

  const detailsUrl = appIntl.urlPrefix + 'account/leads/' + id;
  let currentProgress = 0;
  if (pipeline && currentStage) {
    currentProgress = _findIndex(pipeline.stages, ['id', currentStage]);
  }
  const countStages = pipeline && pipeline.stages ? pipeline.stages.length : 0;
  const progressElements = [];
  for (let j = 0; j < countStages; j++) {
    progressElements.push(
      <div className="pull-left" style={{ width: (1 / countStages) * 100 + '%' }} key={j}>
        <div
          className={'m-r-5 bg-' + (j <= currentProgress ? 'primary-light' : 'master-lighter')}
          style={{ height: '3px' }}
        />
      </div>
    );
  }

  let priorityClass = 'normal ';
  switch (lead.priority) {
    case 0:
      priorityClass = 'low ';
      break;

    case 2:
      priorityClass = 'high ';
      break;

    case 3:
      priorityClass = 'critical ';
      break;
  }

  let label;
  if (isWon) {
    label = (
      <Label className="label-inverse m-r-10" style={{ fontSize: 'inherit' }}>
        {messages.won}
      </Label>
    );
  } else if (isLost) {
    label = (
      <Label className="label-important m-r-10" style={{ fontSize: 'inherit' }}>
        {messages.lost}
      </Label>
    );
  } else if (isDeleted) {
    label = (
      <Label className="label-important m-r-10" style={{ fontSize: 'inherit' }}>
        {messages.deleted}
      </Label>
    );
  } else {
    // label = <Label bsStyle="info" className="m-r-10" style={{ fontSize: 'inherit' }}>{messages.open}</Label>
  }

  let tags;
  if (lead.tags && lead.tags.length) {
    tags = (
      <div className="m-t-10">
        {lead.tags.map(tag => {
          return (
            <React.Fragment key={tag}>
              <Label bsSize="xs" className="m-r-5 m-b-5 inline">
                {tag}
              </Label>{' '}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  const momentWeekAgo = moment()
    .subtract(7, 'days')
    .startOf('day');
  const eventDate = moment(lead.mdate).isAfter(momentWeekAgo) ? (
    <IntlDateRelative date={lead.mdate} />
  ) : (
    <IntlDate date={lead.mdate} />
  );

  let orderType = 'unbekannter Bestelltyp';
  const sampleItemDescription = lead.orders?.[0]?.items?.[0]?.description;
  if (sampleItemDescription) {
    if (sampleItemDescription.startsWith('Druckproduktion')) {
      orderType = 'Druckproduktion';
    } else if (sampleItemDescription.startsWith('PDF-Download')) {
      orderType = 'PDF-Download';
    } else if (sampleItemDescription.startsWith('Upload der Webseite')) {
      orderType = 'Webseite';
    }
  }

  let internalNote = '';
  let shippingMethod;
  const shippingOutput = [];
  let showAddressIfOrder = true;

  if (title === 'Bestellung') {
    showAddressIfOrder = false;
    internalNote = lead.internal_comment;
    shippingMethod = lead.orders?.[0]?.shipping;
  }

  if (shippingMethod !== undefined) {
    let color = 'black';
    let title = shippingMethod;

    if (shippingMethod === 'standard') {
      color = 'grey';
      title = 'Standardversand';
    } else if (shippingMethod === 'express') {
      color = 'orange';
      title = 'Expressversand';
    } else if (shippingMethod === 'overnight') {
      color = '#CF1719';
      title = 'Overnightversand';
    }

    shippingOutput.push(
      <div>
        <i className="fad fa-fw fa-circle" style={{ color }} /> {title}
      </div>
    );
  }

  return {
    pipeline: (
      <div>
        {!!label && <div className="m-b-15">{label}</div>}
        {pipeline ? (
          <>
            <IntlFormInlineSelector /> {getNameInCurrentLanguage(pipeline.name, appIntl)}
          </>
        ) : (
          '-'
        )}
        {progressElements && <Clearfix className="m-t-10">{progressElements}</Clearfix>}
        <p style={{ marginTop: '10px' }}>
          <i className="far fa-tasks" />{' '}
          {getNameInCurrentLanguage(lead.pipeline?.stages?.[currentProgress]?.name, appIntl)}
        </p>
      </div>
    ),
    lead: (
      <>
        <i className="fad fa-fw fa-circle" style={{ color: '#' + getColor(lead.color || 1, 81) }} /> {title}{' '}
        {lead.orders.length > 0 && ' ' + messages.salesDoc.order_number + ' ' + lead.orders[0].id} {tags}
        <Clearfix className="m-t-10">
          <div className="pull-left p-r-5">
            <i className="fal fa-fw fa-clock" />
          </div>
          <OverlayTrigger
            placement="top"
            wrappingComponent={Tooltip}
            overlay={
              <div className="text-left">
                {messages.date_created}: <IntlDateTime date={lead.cdate} />
                <br />
                {messages.date_updated}: <IntlDateTime date={lead.mdate} />
                <br />
                {messages.due_date}: <IntlDateTime date={lead.due_date} />
              </div>
            }
          >
            <div className="pull-left">{eventDate}</div>
          </OverlayTrigger>
        </Clearfix>
      </>
    ),
    orderType: (
      <>
        {title === 'Bestellung' ? <p>{orderType}</p> : <p>-</p>}
        <p>
          <i className="fal fa-fw fa-clock" /> {messages.date_created}: <IntlDateTime date={lead.cdate} />
        </p>
        <div>{shippingOutput !== undefined && shippingOutput}</div>
      </>
    ),
    assignee: assignee ? <ClientListInfo client={assignee} showAddress={false} /> : '-',
    client: (
      <>
        <div>
          {target ? (
            <ClientListInfo
              client={target}
              titleLT={title}
              showAddress={showAddressIfOrder}
              internalNote={internalNote}
            />
          ) : (
            '-'
          )}
        </div>
      </>
    ),
    actions: [
      <Link to={detailsUrl} className="btn-rounded btn btn-sm btn-default" key="edit">
        <i className="fa fa-pencil-alt btn-rounded btn btn-sm btn-default" key="copy" /> {messages.edit}
      </Link>,
    ],
    onClick,
    className: 'lead-row lead-priority-' + priorityClass,
  };
};

export const getChannelTypes = messages => {
  return [
    { label: messages.none, value: null },
    { label: messages.lead_channel_web, value: 'web' },
    { label: messages.lead_channel_phone, value: 'phone' },
    { label: messages.lead_channel_mobile, value: 'mobile' },
    { label: messages.lead_channel_email, value: 'email' },
    { label: messages.lead_channel_personally, value: 'personally' },
  ];
};

export const getChannelTypeLabel = (value, messages) => {
  const res = _find(getChannelTypes(messages), { value });
  return (res && res.label) || '-';
};

export const getLeadPriorities = messages => {
  return [
    {
      icon: <i class="fas fa-fw fa-exclamation-circle text-danger" />,
      color: 'danger',
      label: messages.leadPriority.critical,
      value: 3,
    },
    {
      icon: <i class="far fa-fw fa-arrow-up text-warning" />,
      color: 'warning',
      label: messages.leadPriority.high,
      value: 2,
    },
    {
      icon: <i class="far fa-fw fa-minus" />,
      color: '',
      label: messages.leadPriority.normal,
      value: 1,
    },
    {
      icon: <i class="far fa-fw fa-arrow-down text-success" />,
      color: 'success',
      label: messages.leadPriority.low,
      value: 0,
    },
  ];
};

export const getLeadPriority = (value, messages) => {
  return _find(getLeadPriorities(messages), { value });
};

export const getLeadAssignmentStrategies = () => {
  return [{ label: 'Automatic', value: 'auto' }, { label: 'Pool', value: 'pool' }];
};

export const getLeadAssignmentStrategyLabel = (value, messages) => {
  const res = _find(getLeadAssignmentStrategies(messages), { value });
  return (res && res.label) || '-';
};

export const getLeadEscalationRuleTypes = messages => {
  return [
    {
      label: messages.no_reaction,
      value: 'reaction',
    },
    {
      label: messages.no_update,
      value: 'mdate',
    },
  ];
};

export const getLeadEscalationRuleTypeLabel = (value, messages) => {
  const res = _find(getLeadEscalationRuleTypes(messages), { value });
  return (res && res.label) || '-';
};

export const getLeadEscalationRuleLevels = messages => {
  return [
    {
      label: messages.escalation_level_one,
      value: 'level1',
    },
    {
      label: messages.escalation_level_two,
      value: 'level2',
    },
    {
      label: messages.automatically_terminate_the_lead,
      value: 'terminate',
    },
  ];
};

export const getLeadEscalationRuleLevelLabel = (value, messages) => {
  const res = _find(getLeadEscalationRuleLevels(messages), { value });
  return (res && res.label) || '-';
};

export const getLeadEscalationRuleReactionOptions = () => {
  // value is the tickerCount. Every ticker are 5 minutes

  return [
    {
      label: <FormattedNumber value={30} style="unit" unit="minute" unitDisplay="long" />,
      value: 6,
    },
    {
      label: <FormattedNumber value={1} style="unit" unit="hour" unitDisplay="long" />,
      value: 12,
    },
    {
      label: <FormattedNumber value={2} style="unit" unit="hour" unitDisplay="long" />,
      value: 24,
    },
    {
      label: <FormattedNumber value={3} style="unit" unit="hour" unitDisplay="long" />,
      value: 36,
    },
    {
      label: <FormattedNumber value={4} style="unit" unit="hour" unitDisplay="long" />,
      value: 48,
    },
    {
      label: <FormattedNumber value={1} style="unit" unit="day" unitDisplay="long" />,
      value: 96,
    },
    {
      label: (
        <FormattedList
          type="conjunction"
          value={[
            <FormattedNumber value={1} style="unit" unit="day" key="day" unitDisplay="long" />,
            <FormattedNumber value={4} style="unit" unit="hour" key="hour" unitDisplay="long" />,
          ]}
        />
      ),
      value: 144,
    },
    {
      label: <FormattedNumber value={2} style="unit" unit="day" unitDisplay="long" />,
      value: 192,
    },
  ];
};

export const getLeadEscalationRuleReactionOptionLabel = (value, messages) => {
  const res = _find(getLeadEscalationRuleReactionOptions(messages), { value });
  return (res && res.label) || '-';
};

export const getLeadEscalationRuleMdateOptions = () => {
  // value is the tickerCount. Every ticker are 5 minutes

  return [3, 4, 5, 6, 7].map(counter => ({
    label: <FormattedNumber value={counter} style="unit" unit="day" unitDisplay="long" />,
    value: counter * 288,
  }));
};

export const getLeadEscalationRuleMdateOptionLabel = (value, messages) => {
  const res = _find(getLeadEscalationRuleMdateOptions(messages), { value });
  return (res && res.label) || '-';
};

export const isUserAbleToStartPipeline = (entityId) => {

  let isStartButtonClicked = localStorage.getItem('isStartPipelineButtonClicked' + entityId) === 'true';
  let startButtonClickedTime = localStorage.getItem('startPipelineButtonClickedTime' + entityId);

  if(isStartButtonClicked && startButtonClickedTime) {
    let currentTime = new Date().getTime();
    let timeDifference = currentTime - startButtonClickedTime;
    if (timeDifference < 60000) {
      return false;
    }
  } else {
    return true;
  }

};

export const logStartPipelineButtonClicked = (entityId) => {
  localStorage.setItem('isStartPipelineButtonClicked' + entityId, 'true');
  localStorage.setItem('startPipelineButtonClickedTime' + entityId, '' + new Date().getTime());
}

export const removeStartPipelineButtonClickedLog = (entityId) => {
  localStorage.removeItem('isStartPipelineButtonClicked' + entityId);
  localStorage.removeItem('startPipelineButtonClickedTime' + entityId);
}
